import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Typography,
  styled
} from '@mui/material'
import { memo, useCallback, useMemo, useState } from 'react'
import Icon from '../icon'
import ReadOnlyLabel from '../read-only-label'
import CustomTextField from '../textfield'
import OverlayLoading from '../overlay-loading'
import { deleteEventDetails, editEventDetails } from '../../axios/api/developer'
import { showErrorToast } from '../../utils/utils'
import { toast } from 'react-toastify'

// Detection type options - same as TagCaptureModal
const DETECTION_TYPES = [
  { label: 'CoderScout', value: 'coderscout' },
  { label: 'Chat GPT', value: 'chatgpt' },
  { label: 'Gemini', value: 'gemini' },
  { label: 'Google', value: 'google' }
]

// Styled components
const StyledDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    width: '100%',
    maxWidth: '600px',
    borderRadius: '8px'
  }
}))

const StyledDialogTitle = styled(DialogTitle)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: theme.spacing(2, 3),
  borderBottom: `1px solid ${theme.palette.divider}`
}))

const StyledDialogContent = styled(DialogContent)(({ theme }) => ({
  padding: theme.spacing(3)
}))

const SectionContainer = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  border: `1px solid ${theme.palette.divider}`,
  padding: theme.spacing(2),
  borderRadius: '4px'
}))

const SectionTitle = styled(Typography)(({ theme }) => ({
  margin: 0,
  marginBottom: theme.spacing(1),
  color: theme.palette.primary.main,
  fontWeight: 600
}))

const DetailRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(1),
  marginBottom: theme.spacing(0.5),
  fontSize: '0.9em'
}))

const ImageContainer = styled(Box)(({ theme }) => ({
  marginTop: theme.spacing(1),
  '& img': {
    maxWidth: '100%',
    borderRadius: '4px',
    border: `1px solid ${theme.palette.divider}`
  }
}))

// Helper functions
const parseJson = (str) => {
  try {
    return str ? JSON.parse(str) : null
  } catch (e) {
    return null
  }
}

const formatTime = (seconds) => {
  if (!seconds && seconds !== 0) return 'N/A'
  const mins = Math.floor(seconds / 60)
  const secs = Math.floor(seconds % 60)
  return `${mins}:${secs.toString().padStart(2, '0')}`
}

// Helper to get label from detection type value
const getDetectionLabel = (value) => {
  const type = DETECTION_TYPES.find((t) => t.value === value)
  return type?.label || value || 'N/A'
}

const DeveloperTagModal = ({
  open,
  onClose,
  data: inputData,
  handleRefreshData
}) => {
  // State
  const [isEditing, setIsEditing] = useState(false)
  const [loading, setLoading] = useState(false)
  const [editedDetection, setEditedDetection] = useState('')

  // Parsed data
  const data = useMemo(() => inputData?.eventDetails || {}, [inputData])
  const webcamData = useMemo(() => parseJson(data?.webcam), [data?.webcam])
  const screenData = useMemo(() => parseJson(data?.screen), [data?.screen])

  // Handlers
  const handleEditClick = useCallback(() => {
    setEditedDetection(data?.detectionType || '')
    setIsEditing(true)
  }, [data?.detectionType])

  const handleCancelEdit = useCallback(() => {
    setIsEditing(false)
    setEditedDetection('')
  }, [])

  const handleClose = useCallback(() => {
    setIsEditing(false)
    setEditedDetection('')
    onClose()
  }, [onClose])

  const handleSaveEdit = useCallback(async () => {
    setLoading(true)
    const response = await editEventDetails(inputData?.id, {
      detectionType: editedDetection
    })
    setLoading(false)

    if (response?.isError) {
      showErrorToast(response)
    } else {
      toast.success('Event edited successfully')
      handleRefreshData()
      handleClose()
    }
  }, [inputData?.id, editedDetection, handleRefreshData, handleClose])

  const handleDelete = useCallback(async () => {
    setLoading(true)
    const response = await deleteEventDetails(inputData?.id)
    setLoading(false)

    if (response?.isError) {
      showErrorToast(response)
    } else {
      toast.success('Event deleted successfully')
      handleRefreshData()
      handleClose()
    }
  }, [inputData?.id, handleRefreshData, handleClose])

  return (
    <>
      {loading && <OverlayLoading />}
      <StyledDialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <StyledDialogTitle>
          <Typography variant="h6" fontWeight={600}>
            Developer Tag Details
          </Typography>
          <IconButton onClick={handleClose} size="small">
            <Icon icon="mdi:close" />
          </IconButton>
        </StyledDialogTitle>

        <StyledDialogContent>
          {/* Detection Type Section */}
          <Box sx={{ mb: 2 }}>
            {isEditing ? (
              <CustomTextField
                select
                fullWidth
                size="small"
                value={editedDetection}
                onChange={(e) => setEditedDetection(e.target.value)}
                label="Detection Type"
              >
                {DETECTION_TYPES.map((type) => (
                  <MenuItem key={type.value} value={type.value}>
                    {type.label}
                  </MenuItem>
                ))}
              </CustomTextField>
            ) : (
              <ReadOnlyLabel
                label={'Detection Type'}
                displayValue={getDetectionLabel(data?.detectionType)}
              />
            )}
          </Box>

          {/* Webcam Section */}
          {(webcamData || data?.webcamImageUrl) && (
            <SectionContainer>
              <SectionTitle variant="subtitle1">Webcam</SectionTitle>
              {webcamData && (
                <Box sx={{ fontSize: '0.9em', mb: 1 }}>
                  <DetailRow>
                    <Typography variant="body2" fontWeight={600}>
                      Filename:
                    </Typography>
                    <Typography variant="body2">
                      {webcamData.filename || 'N/A'}
                    </Typography>
                  </DetailRow>
                  <DetailRow>
                    <Typography variant="body2" fontWeight={600}>
                      Duration:
                    </Typography>
                    <Typography variant="body2">
                      {formatTime(webcamData.duration)}
                    </Typography>
                  </DetailRow>
                  <DetailRow>
                    <Typography variant="body2" fontWeight={600}>
                      Time:
                    </Typography>
                    <Typography variant="body2">
                      {formatTime(webcamData.currentTime)}
                    </Typography>
                  </DetailRow>
                </Box>
              )}
              {data?.webcamImageUrl && (
                <ImageContainer>
                  <img src={data.webcamImageUrl} alt="Webcam Capture" />
                </ImageContainer>
              )}
            </SectionContainer>
          )}

          {/* Screen Section */}
          {(screenData || data?.screenShareImageUrl) && (
            <SectionContainer>
              <SectionTitle variant="subtitle1">Screen Share</SectionTitle>
              {screenData && (
                <Box sx={{ fontSize: '0.9em', mb: 1 }}>
                  <DetailRow>
                    <Typography variant="body2" fontWeight={600}>
                      Filename:
                    </Typography>
                    <Typography variant="body2">
                      {screenData.filename || 'N/A'}
                    </Typography>
                  </DetailRow>
                  <DetailRow>
                    <Typography variant="body2" fontWeight={600}>
                      Duration:
                    </Typography>
                    <Typography variant="body2">
                      {formatTime(screenData.duration)}
                    </Typography>
                  </DetailRow>
                  <DetailRow>
                    <Typography variant="body2" fontWeight={600}>
                      Time:
                    </Typography>
                    <Typography variant="body2">
                      {formatTime(screenData.currentTime)}
                    </Typography>
                  </DetailRow>
                </Box>
              )}
              {data?.screenShareImageUrl && (
                <ImageContainer>
                  <img src={data.screenShareImageUrl} alt="Screen Capture" />
                </ImageContainer>
              )}
            </SectionContainer>
          )}
        </StyledDialogContent>

        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          {isEditing ? (
            <>
              <Button variant="outlined" onClick={handleCancelEdit}>
                Cancel
              </Button>
              <Button variant="contained" onClick={handleSaveEdit}>
                Save
              </Button>
            </>
          ) : (
            <>
              <Button
                variant="outlined"
                color="error"
                startIcon={<Icon icon="mdi:delete-outline" />}
                onClick={handleDelete}
              >
                Delete
              </Button>
              <Button
                variant="contained"
                startIcon={<Icon icon="mdi:pencil-outline" />}
                onClick={handleEditClick}
              >
                Edit Detection
              </Button>
            </>
          )}
        </DialogActions>
      </StyledDialog>
    </>
  )
}

export default memo(DeveloperTagModal)
